/**
 * Capacitor plugin using Outsystems Barcode libs
 * Reference docs used to derive types & behavior:
 *   https://www.npmjs.com/package/@capacitor/barcode-scanner
 */

import { CapacitorPluginsBase } from "./capacitorPluginsBase";
import * as datadog from "@/shared/datadog";
import {
  CapacitorBarcodeScannerAndroidScanningLibrary,
  CapacitorBarcodeScannerCameraDirection,
  CapacitorBarcodeScannerScanOrientation,
  CapacitorBarcodeScannerScanResult,
  CapacitorBarcodeScannerTypeHint,
  CapacitorBarcodeScannerTypeHintALLOption
} from "./types";
import Notification from "./notification";

class BarcodeScanner extends CapacitorPluginsBase {
  async scanWithOptions (options?: {
    hint?: CapacitorBarcodeScannerTypeHint;
    scanInstructions?: "Please scan the barcode";
    scanButton?: false;
    scanText?: " "; // label for the scan button if shown
    cameraDirection?: CapacitorBarcodeScannerCameraDirection;
    scanOrientation?: CapacitorBarcodeScannerScanOrientation;
    android?: {
      scanningLibrary?: CapacitorBarcodeScannerAndroidScanningLibrary;
    };
    web?: {
      showCameraSelection?: boolean;
      scannerFPS?: number;
    };
  }): Promise<CapacitorBarcodeScannerScanResult | null> {
    const granted = await this.ensurePermission(true);
    if (!granted) {
      datadog.log("Barcode scanner failed. Unable to access user's camera.", {}, "info");
      Notification.alert({
        message: "Please check your device settings to make sure camera permission is allowed for this app.",
        title: "Unable to access your camera"
      });
      return Promise.resolve(null);
    }
    try {
      const result = await this.plugins.CapacitorBarcodeScanner.scanBarcode({
        hint: options?.hint ?? CapacitorBarcodeScannerTypeHintALLOption.ALL,
        scanInstructions: options?.scanInstructions,
        scanButton: options?.scanButton,
        scanText: options?.scanText,
        cameraDirection: options?.cameraDirection,
        scanOrientation: options?.scanOrientation,
        android: options?.android,
        web: options?.web,
      });
      if (result && result.ScanResult && result.ScanResult.length > 0) {
        return result;
      } else {
        const data = {
          code: result?.ScanResult,
          format: result?.format
        };
        throw new Error(`Scanner returned invalid result. Data: ${JSON.stringify(data)}`);
      }
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes("cancelled")) {
          return Promise.resolve(null);
        }
      }
      datadog.log("Barcode scanner error:", err, "error");
      throw err;
    } finally {
      setTimeout (async () => {
        await this.restoreStatusbarLayout();
      }, 500);
    }
  }

  /** Ask or check for camera permission */
  async ensurePermission (force = false): Promise<boolean> {
    try {
      const checkPermissionResult = await this.plugins.Camera.checkPermissions();
      if (checkPermissionResult && checkPermissionResult.camera && checkPermissionResult.camera === "granted") {
        return true;
      }

      const requestPermissionsResult = await this.plugins.Camera.requestPermissions({ permissions: ["camera"] });
      if (requestPermissionsResult && requestPermissionsResult.camera && requestPermissionsResult.camera === "granted") {
        return true;
      }

      return false;
    } catch (err) {
      datadog.log("Barcode scanner camera checkPermission error:", err, "error");
      return false;
    }
  }

  scanBarcode (): Promise<CapacitorBarcodeScannerScanResult | null> {
    return this.scanWithOptions({
      hint: CapacitorBarcodeScannerTypeHintALLOption.ALL,
      scanInstructions: "Please scan the barcode",
      scanButton: false, scanText: " ",
      cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
      scanOrientation: CapacitorBarcodeScannerScanOrientation.PORTRAIT
    });
  }
}

export default new BarcodeScanner();